import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyS, HeadlineL, LabelM } from '#src/atoms/typography/index.tsx';

const Backdrop = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: fixed;
  inset: 0;
  background: rgba(var(--c-rgb-black), 0.82);
  z-index: 10;
  padding: var(--double-unit);
`;

const Card = styled(Vertical).attrs({ gap: 0 })`
  width: 312px;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-50);
  background: rgba(var(--c-rgb-black), 0.54);
  backdrop-filter: blur(32px);
  overflow: hidden;
`;

const CardContent = styled(Vertical).attrs({ gap: 2 })`
  align-items: flex-start;
  padding: var(--triple-unit) var(--double-unit) var(--double-unit);
  width: 100%;
`;

const WarningBox = styled(Vertical)`
  width: 100%;
  padding: var(--double-unit);
  border-radius: var(--unit);
  background: rgba(var(--c-rgb-yellow-400), 0.12);
  border: 1px solid rgba(var(--c-rgb-yellow-400), 0.3);
  gap: var(--unit);
`;

const HelpCenterLink = styled.a`
  color: var(--c-nd-600);
  text-decoration: underline;
  cursor: pointer;
`;

const CardFooter = styled(Horizontal)`
  padding: var(--double-unit);
  width: 100%;
  gap: var(--unit);
`;

type Props = {
  onGoBack: () => void;
  onSkip: () => void;
};

export const SkipVerificationDialog = ({ onGoBack, onSkip }: Props) => (
  <Backdrop onClick={onGoBack}>
    <Card onClick={e => e.stopPropagation()}>
      <CardContent>
        <HeadlineL color="var(--c-white)" bold>
          <FormattedMessage defaultMessage="Skip verification?" />
        </HeadlineL>
        <WarningBox>
          <LabelM bold color="var(--c-yellow-400)">
            <FormattedMessage defaultMessage="Identity not verified" />
          </LabelM>
          <BodyS color="var(--c-nd-600)">
            <FormattedMessage
              defaultMessage="You have 30 days to verify your identity. In the meantime, you can continue to play but some rewards will be held and will only be awarded after verification of your identity. After this deadline, Sorare Pro will no longer be accessible without verification. More info in the <helpCenterLink>Help Center</helpCenterLink>."
              values={{
                helpCenterLink: chunks => (
                  <HelpCenterLink
                    href="https://sorare.com/help"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </HelpCenterLink>
                ),
              }}
            />
          </BodyS>
        </WarningBox>
      </CardContent>
      <CardFooter>
        <Button size="medium" color="quaternary" onClick={onGoBack} fullWidth>
          <FormattedMessage defaultMessage="Go back" />
        </Button>
        <Button size="medium" color="red" onClick={onSkip} fullWidth>
          <FormattedMessage defaultMessage="Skip" />
        </Button>
      </CardFooter>
    </Card>
  </Backdrop>
);
